// The technical session-status digest (Brief 5 / pt11) — the babysitter's
// "catch me up technically" view, reduced to a structured object the panel
// renders densely. Distinct from the conversational operator chat: this is a
// scannable, factual STATUS REPORT — connection, agent activity, recent
// read/write operations, any caught error + how it was handled, the open
// question(s) awaiting the user, and the run/budget state when present.
//
// FACTS ONLY (pt13 — never predict): every field is derived from observable,
// already-landed data the session surface holds (the live stream health, the
// presence row, the trace timeline, the steering asides, the spend ledger). No
// guesses about what the agent WILL do, no synthesized rationale. Where a fact
// isn't known we say so (`""` / a null section), never a confident invention.
//
// Pure + UI-free + global-free: no React, no DOM, no clock read
// except the injectable `now`. The panel in @waytide/ui turns these keys into
// token-mapped chrome; this module owns the DERIVATION, unit-tested directly so
// the honesty rules can't silently regress.

import type { StreamHealth } from "./sse";
import type {
  NeedsInputReason,
  SessionPresence,
  SteeringDecision,
  TraceEvent,
} from "./types";
import { tracePresentation, isIntervention } from "./tracePresentation";

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;

// ─── Connection ───────────────────────────────────────────────────────────

/** The connection facet of the digest: the live-stream health verbatim plus a
 * concrete last-frame age, so a stalled feed reads as stalled with a number,
 * never as a quiet-but-live one (pt11 honesty, mirroring StreamHealth). */
export interface ConnectionStatus {
  /** The live transport health, taken as-is from the channel. */
  health: StreamHealth;
  /** True only when `health === "live"` — the one state that is trustworthy
   * right now (kept explicit so the panel doesn't re-derive the rule). */
  live: boolean;
  /** Epoch-ms of the last live frame on this channel, or null if none yet. */
  lastEventAt: number | null;
  /** "" when no frame yet; else a short age of the last frame ("just now" /
   * "5m" / "2h") — the staleness as a concrete value. */
  lastFrameAge: string;
}

// ─── Agent activity ─────────────────────────────────────────────────────────

/** The agent facet: what the watched session is doing right now, from its
 * presence row + the newest trace row. Plain status; the last operation is a
 * fact that already happened, never a prediction of the next one. */
export interface AgentActivity {
  /** The presence status verbatim ("running" | "idle" | "paused" | "halted" |
   * "needs_input" | …) — open vocabulary, treated calmly (the panel labels it). */
  status: string;
  /** True when the session is actively running. */
  running: boolean;
  /** True when the session is stopped (paused or halted). */
  stopped: boolean;
  /** The harness + model the session runs ("claudecode" / a model id), or "" /
   * null when the bridge hasn't named them yet. Facts, passed through. */
  harness: string;
  model: string;
  /** The most recent operation the session performed (the newest trace row's
   * kind label + one-line summary), or null when the timeline is empty. */
  lastOperation: OperationLine | null;
}

// ─── Operations (read / write) ──────────────────────────────────────────────

/** One operation line for the digest — a trace row reduced to its label, its
 * one-line summary, and whether it is a WRITE. Facts; no rationale. */
export interface OperationLine {
  /** The trace `kind` verbatim (so the panel can map an accent/tone). */
  kind: string;
  /** The short presentation label for the kind ("commit", "proof", "flag", …). */
  label: string;
  /** The one-line human summary the trace row carries (may be ""). */
  summary: string;
  /** Epoch-micros the row was recorded (the trace wire convention). */
  atMicros: number;
  /** True when this operation WROTE into the project/session (a commit, an
   * operator halt) rather than read/observed. Drives the read-vs-write split
   * the panel shows — the read-only-99% canon (pt9) made concrete here. */
  write: boolean;
}

// ─── Caught errors ──────────────────────────────────────────────────────────

/** A caught problem + how it was handled (pt11: "any caught error + how it was
 * handled"). Derived from the trace's intervention rows (an operator flag/halt
 * is Waytide CATCHING something and acting); the "handled" line is the fact of
 * the intervention, never a predicted fix. */
export interface CaughtError {
  /** The intervention kind ("operator_halt" | "operator_flag" | "reach"). */
  kind: string;
  /** The short label ("halt" | "flag" | "reach"). */
  label: string;
  /** What was caught — the trace row's summary (may be ""). */
  summary: string;
  /** How it was handled, in plain words, from the kind: a halt STOPPED the
   * work; a flag RAISED a warning; a reach ESCALATED to the human. A fact about
   * what the operator did, not a prediction of the outcome. */
  handled: string;
  atMicros: number;
}

// ─── Outstanding question(s) ─────────────────────────────────────────────────

/** A question awaiting the user (pt11). Two honest sources, both already-landed
 * facts: (a) the presence row says `needs_input` (with its typed reason), and
 * (b) an unresolved steering decision is open in the thread. Never a predicted
 * "they'll probably ask" — only a question that is actually pending. */
export interface OutstandingQuestion {
  /** "presence" (the session reported needs_input) or "steering" (an open
   * steering decision aside). */
  source: "presence" | "steering";
  /** The plain prompt: the steering decision's prompt, or the typed
   * needs-input reason in words ("Needs a decision" …). */
  prompt: string;
  /** For a steering question, the count of offered options (0 for presence). */
  optionCount: number;
}

// ─── Run / budget ────────────────────────────────────────────────────────────

/** The run/budget facet when present (pt11: "run/budget state if available").
 * All money is server-computed micros, passed through — this never sums or
 * predicts spend; it reports the ledger fact and whether the budget is
 * exhausted (the exhaustion that STOPS the agent — pt: money model). */
export interface BudgetStatus {
  /** Spend attributed to this session so far, in micros (>= 0). */
  spentMicros: number;
  /** The session's budget cap in micros, or null when no cap is set. */
  capMicros: number | null;
  /** cap - spent floored at 0, or null when there is no cap. */
  remainingMicros: number | null;
  /** True when a cap exists and nothing is left — the structural stop. */
  exhausted: boolean;
}

// ─── The digest ──────────────────────────────────────────────────────────────

/** The structured technical status digest (pt11) — the panel's whole input.
 * Every field is a fact derived from already-available session data. */
export interface SessionStatusDigest {
  connection: ConnectionStatus;
  agent: AgentActivity;
  /** The recent operations, newest-first, already split by read/write via each
   * line's `write` flag (the panel groups them). Capped to a scannable few. */
  recentOperations: OperationLine[];
  /** The most recent caught error + how it was handled, or null when none. */
  caughtError: CaughtError | null;
  /** Every question awaiting the user right now (presence + open steering),
   * newest-relevant first. Empty when nothing is pending. */
  outstanding: OutstandingQuestion[];
  /** The run/budget state, or null when no spend/cap is known for the session. */
  budget: BudgetStatus | null;
}

/** The already-available inputs the digest is derived from — all facts the
 * session surface (web SessionView / shared ChannelView) already holds. Only
 * `health`, `lastEventAt`, `session`, and `activity` are required; the rest are
 * optional so a surface that lacks a fact (no steering parse, no spend read)
 * still gets a truthful digest with those sections empty. */
export interface SessionStatusInput {
  /** The live transport health from the channel hook. */
  health: StreamHealth;
  /** Epoch-ms of the last live frame, or null. */
  lastEventAt: number | null;
  /** The session's presence row, or null when not yet observed. */
  session: SessionPresence | null;
  /** The trace timeline for the session, oldest→newest (the channel hook's
   * `activity`). The digest reverses it for the newest-first operations. */
  activity: TraceEvent[];
  /** The open (unresolved) steering decisions in the thread, if the surface
   * parsed them (web does; the shared view may pass []). */
  openDecisions?: SteeringDecision[];
  /** This session's attributed spend in micros, when read (else omitted). */
  spentMicros?: number;
  /** This session's budget cap in micros, when known (else omitted). */
  capMicros?: number;
  /** Now, epoch-ms — injectable for deterministic tests; defaults to the
   * wall clock. */
  now?: number;
  /** How many recent operations to surface. Default 5 — scannable, not a log. */
  maxOperations?: number;
}

/** The kinds that WRITE into the project/session, as opposed to read/observe.
 * `commit` lands code; `operator_halt` writes a control token (it STOPS the
 * agent). Everything else in the trace vocabulary (proof, suggestion, flag,
 * reach, corpus, narration) is a read/record. Kept narrow + explicit so the
 * read-only-99% claim (pt9) is honest, not hand-waved. */
const WRITE_KINDS = new Set(["commit", "operator_halt"]);

function isWriteKind(kind: string): boolean {
  return WRITE_KINDS.has(kind);
}

/** A short relative age for an epoch-ms instant ("just now" / "5m" / "2h"), or
 * "" when the instant is missing/garbled. Mirrors the stream-health phrasing so
 * the digest speaks one time-vocabulary with the rest of the surface. */
function ageMs(atMs: number | null, now: number): string {
  if (atMs == null || !Number.isFinite(atMs)) return "";
  const diff = now - atMs;
  if (diff < MINUTE_MS) return "just now";
  if (diff < HOUR_MS) return `${Math.floor(diff / MINUTE_MS)}m`;
  return `${Math.floor(diff / HOUR_MS)}h`;
}

/** The plain "how it was handled" line for an intervention kind — a fact about
 * what the operator DID, never a predicted outcome. */
function handledLine(kind: string): string {
  switch (kind) {
    case "operator_halt":
      return "stopped the work";
    case "operator_flag":
      return "raised a warning";
    case "reach":
      return "escalated to you";
    default:
      return "handled";
  }
}

/** The plain words for a typed needs-input reason (pt11 outstanding question).
 * Self-contained (core is UI-free, so it can't borrow the UI presence model);
 * an unknown/blank reason falls back to a calm generic line. */
function reasonPrompt(reason: NeedsInputReason | undefined): string {
  switch (reason) {
    case "decision":
      return "Needs a decision";
    case "permission":
      return "Waiting on permission";
    case "blocked":
      return "Blocked — needs you";
    case "clarification":
      return "Needs clarification";
    default:
      return "Waiting on you";
  }
}

/** Reduce one trace row to an operation line (label + write classification). */
function toOperation(event: TraceEvent): OperationLine {
  const p = tracePresentation(event.kind);
  return {
    kind: event.kind,
    label: p.label,
    summary: event.summary,
    atMicros: event.created_at,
    write: isWriteKind(event.kind),
  };
}

/**
 * Derive the technical status digest (pt11) from the session's already-available
 * data. PURE + total: every section is a fact or an honest empty; nothing is
 * predicted (pt13). The order of operations:
 *   - connection: the stream health verbatim + a concrete last-frame age.
 *   - agent: the presence status + harness/model + the newest trace row as the
 *     last operation.
 *   - recentOperations: the newest `maxOperations` trace rows, newest-first,
 *     each tagged read/write.
 *   - caughtError: the most recent intervention row (flag/halt/reach) + how it
 *     was handled, or null.
 *   - outstanding: every pending question — an open steering decision and/or a
 *     `needs_input` presence row — newest-relevant first, or empty.
 *   - budget: the server-computed spend/cap when known, with the exhaustion
 *     flag, or null.
 */
export function deriveSessionStatus(
  input: SessionStatusInput,
): SessionStatusDigest {
  const now = input.now ?? Date.now();
  const max = input.maxOperations ?? 5;
  const session = input.session;
  const activity = input.activity;

  // Connection — health as-is + how stale the last frame is.
  const connection: ConnectionStatus = {
    health: input.health,
    live: input.health === "live",
    lastEventAt: input.lastEventAt,
    lastFrameAge: ageMs(input.lastEventAt, now),
  };

  // Newest-first view of the timeline (the wire is oldest→newest).
  const newestFirst = [...activity].reverse();

  // Agent — presence status + harness/model + the newest op as "last operation".
  const status = session?.status ?? "";
  const agent: AgentActivity = {
    status,
    running: status === "running",
    stopped: status === "paused" || status === "halted",
    harness: session?.harness ?? "",
    model: session?.model ?? "",
    lastOperation: newestFirst.length > 0 ? toOperation(newestFirst[0]) : null,
  };

  // Recent operations — a scannable few, newest-first, each read/write tagged.
  const recentOperations = newestFirst.slice(0, Math.max(0, max)).map(toOperation);

  // Caught error — the most recent intervention (flag/halt/reach) + how handled.
  const interventionRow = newestFirst.find((e) => isIntervention(e.kind)) ?? null;
  const caughtError: CaughtError | null = interventionRow
    ? {
        kind: interventionRow.kind,
        label: tracePresentation(interventionRow.kind).label,
        summary: interventionRow.summary,
        handled: handledLine(interventionRow.kind),
        atMicros: interventionRow.created_at,
      }
    : null;

  // Outstanding — open steering decisions first (they carry options to act on),
  // then a needs_input presence row. Both are landed facts, never predictions.
  const outstanding: OutstandingQuestion[] = [];
  for (const d of input.openDecisions ?? []) {
    outstanding.push({
      source: "steering",
      prompt: d.prompt,
      optionCount: d.options.length,
    });
  }
  if (session && session.status === "needs_input") {
    outstanding.push({
      source: "presence",
      prompt: reasonPrompt(session.needs_input_reason),
      optionCount: 0,
    });
  }

  // Budget — server-computed micros, passed through; the exhaustion is the
  // structural stop. Only present when the surface read a spend or a cap.
  let budget: BudgetStatus | null = null;
  if (input.spentMicros !== undefined || input.capMicros !== undefined) {
    const spentMicros = Math.max(0, input.spentMicros ?? 0);
    const capMicros =
      input.capMicros !== undefined ? Math.max(0, input.capMicros) : null;
    const remainingMicros =
      capMicros !== null ? Math.max(0, capMicros - spentMicros) : null;
    budget = {
      spentMicros,
      capMicros,
      remainingMicros,
      exhausted: capMicros !== null && remainingMicros === 0,
    };
  }

  return {
    connection,
    agent,
    recentOperations,
    caughtError,
    outstanding,
    budget,
  };
}
